import { Component, ViewChild, ElementRef, Input, OnChanges } from '@angular/core';
import * as Highcharts from 'highcharts';
import { EarSideEnum } from '../../../models/enums/ear-side-enum';
import { NoahSessionGraphViewModel, NoahSessionGraphPointViewModel } from '../../../models/noah-sessions';
import { NoahSessionPointStatusEnum } from '../../../models/enums';

@Component({
    selector: 'noah-graph',
    templateUrl: './noah-graph.component.html',
    styleUrls: ['./noah-graph.component.scss'],
    standalone: false
})
export class NoahGraphComponent implements OnChanges {
    @ViewChild('chartContainer', { static: true }) private chartContainer: ElementRef;

    private color: string;
    private frequencyItems: Array<string> = ['125', '250', '500', '750', '1K', '1.5K', '2K', '3K', '4K','5K', '6K', '6.3K', '7.1K', '8K', '9K','10K','11.2K','12K','12.5K','14K', '16K','18K','20K'];
    private acMappedData: any[];
    private bcMappedData: any[];
    private mclMappedData: any[];
    private uclMappedData: any[];
    private sfMappedData: any[];
    private sfaMappedData: any[];

    @Input() public graphModel: NoahSessionGraphViewModel;
    @Input() public side: EarSideEnum;

    public constructor() {
        this.setupSymbols();
    }

    public ngOnChanges() {
        if (!this.graphModel) return;

        switch (this.side) {
            case EarSideEnum.Right:
                this.color = '#DA4C60';
                break;
            case EarSideEnum.Left:
                this.color = '#167DD3';
                break;
        }

        this.graphModel = JSON.parse(JSON.stringify(this.graphModel));

        this.applyIntermediateValues();

        this.mapData();

        this.drawGraph();
    }

    private drawGraph() {
        Highcharts.chart(
            this.chartContainer.nativeElement, {
                chart: {
                    spacingBottom: 0,
                    spacingLeft: 0,
                    spacingRight: 2,
                    spacingTop: 2
                },
                tooltip: {
                    enabled: false
                },
                title: {
                    text: ''
                },
                xAxis: {
                    gridLineWidth: 1,
                    categories: this.frequencyItems,
                    labels: {
                        style: {
                            fontSize: '8'
                        },
                        autoRotationLimit: 40,
                    },
                    min: 0,
                    max: this.frequencyItems.length - 3,
                    tickInterval: 2
                },
                yAxis: {
                    gridLineWidth: 1,
                    title: false,
                    labels: {
                        style: {
                            fontSize: '8'
                        },
                        x: -5,
                        y: 5
                    },
                    min: -10,
                    max: 120,
                    tickInterval: 10,
                    reversed: true
                } as unknown,
                plotOptions: {
                    series: {
                        connectNulls: false,
                        dataLabels: {
                            enabled: false
                        },
                        showInLegend: false,
                        lineWidth: 1,
                        marker: {
                            fillColor: 'white',
                            lineColor: this.color,
                            lineWidth: 1
                        }
                    },
                    line: {
                        color: this.color
                    }
                },
                series: [{
                    type: undefined,
                    name: '',
                    data: this.acMappedData
                }, {
                    type: undefined,
                    name: '',
                    lineWidth: 0,
                    data: this.bcMappedData
                }, {
                    type: undefined,
                    name: '',
                    data: this.mclMappedData
                }, {
                    type: undefined,
                    name: '',
                    data: this.uclMappedData
                }, {
                    type: undefined,
                    name: '',
                    data: this.sfMappedData
                }, {
                    type: undefined,
                    name: '',
                    data: this.sfaMappedData
                }
                ],
                credits: {
                    enabled: false
                }
            });
    }

    private applyIntermediateValues() {        
        const length = this.frequencyItems.length + 0;

        for (var i = 0; i < length; i++) {

            if (i + 1 < length) {
                const actualIndex = i * 2;
                this.frequencyItems.splice(actualIndex + 1, 0, '');

                for (let key of Object.keys(this.graphModel)) {
                    this.graphModel[key].splice(actualIndex + 1, 0, {
                        value: null,
                        status: null,
                        isMasked: false
                    });

                    var leftValues = this.graphModel[key].slice(actualIndex + 1);
                    var nrIndex = leftValues.findIndex(x => x.status == NoahSessionPointStatusEnum.Nr || x.status == NoahSessionPointStatusEnum.Unm);
                    var currentItem = this.graphModel[key][actualIndex];

                    var nextNotEmptyNotNr = (currentItem.value != null && (currentItem.status != NoahSessionPointStatusEnum.Nr && currentItem.status != NoahSessionPointStatusEnum.Unm))
                        && leftValues.slice(0, nrIndex == -1 ? leftValues.length : nrIndex).find(x => x.value != null);

                    if (nextNotEmptyNotNr) {
                        const itemIndex = this.graphModel[key].indexOf(nextNotEmptyNotNr);

                        var step = (nextNotEmptyNotNr.value - currentItem.value) / ((itemIndex - actualIndex) * 2 - 2);
                        this.graphModel[key][actualIndex + 1].value = currentItem.value + step;

                        if (this.graphModel[key][actualIndex + 2].value == null) {
                            this.graphModel[key][actualIndex + 2].value = currentItem.value + (step * 2);
                        }
                    }
                }
            }
        }
    }

    private mapToGraphData(points: NoahSessionGraphPointViewModel[], type: string, side: EarSideEnum): Array<any> {
        var mapped = points.map(x => {
            var symbol = type + (side == EarSideEnum.Left ? 'Left' : 'Right');

            if (x.isMasked) {
                symbol += 'Masked';
            }

            switch (x.status) {
                case NoahSessionPointStatusEnum.Regular: {
                    break;
                }
                case NoahSessionPointStatusEnum.Nr: {
                    symbol += 'NR';
                    break;
                }
                case NoahSessionPointStatusEnum.Unm: {
                    symbol += 'UNM';
                    break;
                }
                default: {
                    symbol = '';
                    break;
                }
            }

            const width = this.getSymbolWidth(symbol);

            return {
                y: x.value,
                marker: {
                    enabled: !!symbol,
                    symbol,
                    lineWidth: width,
                    states: {
                        hover: {
                            enabled: !!symbol && !!width
                        },
                        inactive: {
                            opacity: 0
                        }
                    },
                    dataLabels: {
                        enabled: !!symbol
                    }
                }
            }
        });

        return mapped;
    }

    private adjustScaling() {
        for (let key of Object.keys(this.graphModel)) {
            this.graphModel[key].splice(1, 0, {
                value: null,
                status: null,
                isMasked: false
            });

            this.graphModel[key].splice(3, 0, {
                value: null,
                status: null,
                isMasked: false
            });
        }
    }

    private mapData() {

        this.acMappedData = this.mapToGraphData(this.graphModel.acValues, 'ac', this.side);
        this.bcMappedData = this.mapToGraphData(this.graphModel.bcValues, 'bc', this.side);
        this.mclMappedData = this.mapToGraphData(this.graphModel.mclValues, 'mcl', this.side);
        this.uclMappedData = this.mapToGraphData(this.graphModel.uclValues, 'ucl', this.side);
        this.sfMappedData =  this.mapToGraphData(this.graphModel.sfValues, 'sf', this.side);
        this.sfaMappedData = this.mapToGraphData(this.graphModel.sfaValues, 'sfa', this.side);

        const maxFrequencyPosition = Math.max(this.getMaxFrequencyPosition(this.acMappedData), this.getMaxFrequencyPosition(this.bcMappedData), this.getMaxFrequencyPosition(this.mclMappedData),
            this.getMaxFrequencyPosition(this.uclMappedData), this.getMaxFrequencyPosition(this.sfMappedData), this.getMaxFrequencyPosition(this.sfaMappedData));

       this.frequencyItems = this.frequencyItems.slice(0, maxFrequencyPosition + 6);
    }

    private getMaxFrequencyPosition(data: any[]) {
        const mappedFrequencyPositions = data.reduce((arr, element, index) => {
            if (element.marker.enabled) {
                arr.push(index);
            }
            return arr;
        }, []);

        return (mappedFrequencyPositions.length) ? Math.ceil(mappedFrequencyPositions.pop()) : Math.ceil((this.frequencyItems.length / 2) + 1);
    }

    private getSymbolWidth(symbol: string) {
        const emptySymbols = ['bcRightUNM', 'bcRightMaskedUNM', 'bcLeftUNM', 'bcLeftMaskedUNM', 'mclRightUNM',
            'mclRightMasked', 'mclRightMaskedNR', 'mclRightMaskedUNM', 'mclLeftUNM', 'mclLeftMasked', 'mclLeftMaskedNR',
            'mclLeftMaskedUNM', 'sfaLeftUNM', 'sfaLeftMasked', 'sfaLeftMaskedNR', 'sfaLeftMaskedUNM', 'sfaRightUNM', 'sfaRightMasked',
            'sfaRightMaskedNR', 'sfaRightMaskedUNM', 'sfLeftUNM', 'sfLeftMasked', 'sfLeftMaskedNR', 'sfLeftMaskedUNM', 'sfRightUNM',
            'sfRightMasked', 'sfRightMaskedNR', 'sfRightMaskedUNM', 'uclRightUNM', 'uclRightMasked', 'uclRightMaskedNR',
            'uclRightMaskedUNM', 'uclLeftUNM', 'uclLeftMasked', 'uclLeftMaskedNR', 'uclLeftMaskedUNM'];

        if (emptySymbols.indexOf(symbol) == -1) {
            return 1;
        }
        else {
            return 0;
        }
    }

    private setupSymbols() {
        var squareFunc = function (x, y, w, h) {
            return ['M', x, y, 'L', x + w, y, 'L', x + w, y + h, 'L', x, y + h, 'z'];
        };

        var circleFunc = function (x, y, w, h) {
            return ['M', x + w / 10, y + h / 2, 'a', w / 5 * 2, w / 5 * 2 + ' 0 1', '0 ' + w / 5 * 4, 0, 'a', w / 5 * 2, w / 5 * 2 + ' 0 1', '0 -' + w / 5 * 4, 0, 'z'];
        };

        // ac right
        Highcharts.SVGRenderer.prototype.symbols["acRight"] = circleFunc;
        Highcharts.SVGRenderer.prototype.symbols["acRightNR"] = function (x, y, w, h) {
            return ['M', x + w / 10, y + h / 2, 'a', w / 5 * 2, w / 5 * 2 + ' 0 1', '0 ' + w / 5 * 4, 0, 'a', w / 5 * 2, w / 5 * 2 + ' 0 1', '0 -' + w / 5 * 4, 0,
                'M', x + w / 4, y + h - (h / 4), 'L', x, y + h, 'M', x, y + h, x - 2, y + h - 2, 'L', x - 2, y + h + 2, 'L', x + 2, y + h + 2, 'z'];
        };
        Highcharts.SVGRenderer.prototype.symbols["acRightUNM"] = function (x, y, w, h) {
            return ['M', x + w / 10, y + h / 2, 'a', w / 5 * 2, w / 5 * 2 + ' 0 1', '0 ' + w / 5 * 4, 0, 'a', w / 5 * 2, w / 5 * 2 + ' 0 1', '0 -' + w / 5 * 4, 0,
                'M', x + w / 5, y + h / 5, 'L', x, y, 'M', x, y, 'L', x - 2, y + 2, 'L', x - 2, y - 2, 'L', x + 2, y - 2, 'z'];
        };
        Highcharts.SVGRenderer.prototype.symbols["acRightMasked"] = function (x, y, w, h) {
            return ['M', x + w / 2, y - 2, 'L', x, y + h, 'L', x + w, y + h, 'L', x + w / 2, y - 2, 'z'];
        };
        Highcharts.SVGRenderer.prototype.symbols["acRightMaskedNR"] = function (x, y, w, h) {
            return ['M', x + w / 2, y - 2, 'L', x, y + h, 'L', x + w, y + h, 'L', x + w / 2, y - 2,
                'M', x, y + h, 'L', x - 2, y + h - 2, 'L', x - 2, y + h + 2, 'L', x + 2, y + h + 2, 'z'];
        };
        Highcharts.SVGRenderer.prototype.symbols["acRightMaskedUNM"] = function (x, y, w, h) {
            return ['M', x + w / 2, y - 2, 'L', x, y + h, 'L', x + w, y + h, 'L', x + w / 2, y - 2,
                'M', x + w / 4, y + h / 2, 'L', x - 1, y, 'M', x - 1, y, 'L', x - 3, y + 2, 'L', x - 3, y - 2, 'L', x + 1, y - 1, 'z'];
        };

        //ac left
        Highcharts.SVGRenderer.prototype.symbols["acLeft"] = function (x, y, w, h) {
            return ['M', x + w, y, 'L', x, y + h, 'M', x, y, 'L', x + w, y + h, 'z'];
        };
        Highcharts.SVGRenderer.prototype.symbols["acLeftNR"] = function (x, y, w, h) {
            return ['M', x + w, y, 'L', x, y + h, 'M', x, y, 'L', x + w, y + h,
                'M', x + w, y + h, 'L', x + w + 2, y + h - 1, 'L', x + w + 2, y + h + 2, 'L', x + w - 1, y + h + 2, 'z'];
        };
        Highcharts.SVGRenderer.prototype.symbols["acLeftUNM"] = function (x, y, w, h) {
            return ['M', x + w, y, 'L', x, y + h, 'M', x, y, 'L', x + w, y + h,
                'M', x + w, y, 'L', x + w + 2, y + 1, 'L', x + w + 2, y - 2, 'L', x + w - 1, y - 2, 'z'];
        };
        Highcharts.SVGRenderer.prototype.symbols["acLeftMasked"] = squareFunc;

        Highcharts.SVGRenderer.prototype.symbols["acLeftMaskedNR"] = function (x, y, w, h) {
            return ['M', x, y, 'L', x + w, y, 'L', x + w, y + h, 'L', x, y + h, 'L', x, y,
                'M', x + w + 1, y + h + 1, 'L', x + w + 3, y + h - 1, 'L', x + w + 3, y + h + 3, 'L', x + w - 1, y + h + 3, 'z'];
        };
        Highcharts.SVGRenderer.prototype.symbols["acLeftMaskedUNM"] = function (x, y, w, h) {
            return ['M', x, y, 'L', x + w, y, 'L', x + w, y + h, 'L', x, y + h, 'L', x, y,
                'M', x + w + 1, y - 1, 'L', x + w + 3, y + 1, 'L', x + w + 3, y - 3, 'L', x + w - 1, y - 3, 'z'];
        };

        // bc right
        Highcharts.SVGRenderer.prototype.symbols["bcRight"] = function (x, y, w, h) {
            return ['M', x + w / 2, y, 'L', x, y + h / 2, 'M', x, y + h / 2, 'L', x + w / 2, y + h, 'z'];
        };
        Highcharts.SVGRenderer.prototype.symbols["bcRightNR"] = function (x, y, w, h) {
            return ['M', x + w / 2, y, 'L', x, y + h / 2, 'M', x, y + h / 2, 'L', x + w / 2, y + h,
                'M', x + w / 2, y + h, 'L', x + w / 4 - 1, y + h + 3, 'M', x + w / 4 - 1, y + h + 3, 'L', x + w / 4 - 2, y + h + 2, 'L', x + w / 4 - 2, y + h + 4, 'L', x + w / 4 + 1, y + h + 4, 'z'];
        };

        Highcharts.SVGRenderer.prototype.symbols["bcRightUNM"] = circleFunc;
        Highcharts.SVGRenderer.prototype.symbols["bcRightMasked"] = function (x, y, w, h) {
            return ['M', x + w / 3, y, 'L', x, y, 'L', x, y + h, 'L', x + w / 3, y + h, 'M', 'z'];
        };
        Highcharts.SVGRenderer.prototype.symbols["bcRightMaskedNR"] = function (x, y, w, h) {
            return ['M', x + w / 3, y, 'L', x, y, 'L', x, y + h, 'L', x + w / 3, y + h,
                'M', x - 1, y + h + 1, 'L', x - 2, y + h, 'L', x - 2, y + h + 2, 'L', x + 1, y + h + 2, 'z'];
        };
        Highcharts.SVGRenderer.prototype.symbols["bcRightMaskedUNM"] = circleFunc;


        // bc left
        Highcharts.SVGRenderer.prototype.symbols["bcLeft"] = function (x, y, w, h) {
            return ['M', x + w / 2, y, 'L', x + w, y + h / 2, 'M', x + w, y + h / 2, 'L', x + w / 2, y + h, 'z'];
        };
        Highcharts.SVGRenderer.prototype.symbols["bcLeftNR"] = function (x, y, w, h) {
            return ['M', x + w / 2, y, 'L', x + w, y + h / 2, 'M', x + w, y + h / 2, 'L', x + w / 2, y + h,
                'M', x + w / 2, y + h, 'L', x + w / 2 + 3, y + h + 3, 'M', x + w / 2 + 3, y + h + 3, 'L', x + w / 2 + 4, y + h + 1, 'L', x + w / 2 + 4, y + h + 4, 'L', x + w / 2 + 1, y + h + 4, 'z'];
        };
        Highcharts.SVGRenderer.prototype.symbols["bcLeftUNM"] = circleFunc;
        Highcharts.SVGRenderer.prototype.symbols["bcLeftMasked"] = function (x, y, w, h) {
            return ['M', x + w / 3 * 2, y, 'L', x + w, y, 'L', x + w, y + h, 'L', x + w / 3 * 2, y + h, 'M', x + w / 3 * 2, y + h, 'z'];
        };
        Highcharts.SVGRenderer.prototype.symbols["bcLeftMaskedNR"] = function (x, y, w, h) {
            return ['M', x + w / 3 * 2, y, 'L', x + w, y, 'L', x + w, y + h, 'L', x + w / 3 * 2, y + h,
                'M', x + w + 1, y + h + 1, 'L', x + w + 2, y + h, 'L', x + w + 2, y + h + 2, 'L', x + w - 1, y + h + 2, 'z'];
        };
        Highcharts.SVGRenderer.prototype.symbols["bcLeftMaskedUNM"] = circleFunc;


        // mcl right
        Highcharts.SVGRenderer.prototype.symbols["mclRight"] = function (x, y, w, h) {
            return ['M', x, y, 'L', x, y + h, 'M', x, y, 'L', x + w / 2, y + h - 1, 'M', x + w / 2, y + h - 1, 'L', x + w, y, 'M', x + w, y, 'L', x + w, y + h, 'z'];
        };
        Highcharts.SVGRenderer.prototype.symbols["mclRightNR"] = function (x, y, w, h) {
            return ['M', x, y, 'L', x, y + h, 'M', x, y, 'L', x + w / 2, y + h - 1, 'M', x + w / 2, y + h - 1, 'L', x + w, y, 'M', x + w, y, 'L', x + w, y + h,
                'M', x + w + 2, y + h, 'L', x + w, y + h + 2, 'L', x + w - 2, y + h, 'z'];
        };
        Highcharts.SVGRenderer.prototype.symbols["mclRightUNM"] = circleFunc;
        Highcharts.SVGRenderer.prototype.symbols["mclRightMasked"] = circleFunc;
        Highcharts.SVGRenderer.prototype.symbols["mclRightMaskedNR"] = circleFunc;
        Highcharts.SVGRenderer.prototype.symbols["mclRightMaskedUNM"] = circleFunc;

        // mcl left
        Highcharts.SVGRenderer.prototype.symbols["mclLeft"] = function (x, y, w, h) {
            return ['M', x, y, 'L', x, y + h, 'M', x, y, 'L', x + w / 2, y + h - 1, 'M', x + w / 2, y + h - 1, 'L', x + w, y, 'M', x + w, y, 'L', x + w, y + h, 'z'];
        };
        Highcharts.SVGRenderer.prototype.symbols["mclLeftNR"] = function (x, y, w, h) {
            return ['M', x, y, 'L', x, y + h, 'M', x, y, 'L', x + w / 2, y + h - 1, 'M', x + w / 2, y + h - 1, 'L', x + w, y, 'M', x + w, y, 'L', x + w, y + h,
                'M', x + w + 2, y + h, 'L', x + w, y + h + 2, 'L', x + w - 2, y + h, 'z'];
        };
        Highcharts.SVGRenderer.prototype.symbols["mclLeftUNM"] = circleFunc;
        Highcharts.SVGRenderer.prototype.symbols["mclLeftMasked"] = circleFunc;
        Highcharts.SVGRenderer.prototype.symbols["mclLeftMaskedNR"] = circleFunc;
        Highcharts.SVGRenderer.prototype.symbols["mclLeftMaskedUNM"] = circleFunc;


        // ucl right
        Highcharts.SVGRenderer.prototype.symbols["uclRight"] = function (x, y, w, h) {
            return ['M', x - 1, y, 'L', x - 1, y + h, 'M', x - 1, y, 'L', x + w + 1, y, 'M', x + w / 2, y, 'L', x + w / 2, y + h - 1, 'M', x + w + 1, y, 'L', x + w, y + h, 'z'];
        };
        Highcharts.SVGRenderer.prototype.symbols["uclRightNR"] = function (x, y, w, h) {
            return ['M', x - 1, y, 'L', x - 1, y + h, 'M', x - 1, y, 'L', x + w + 1, y, 'M', x + w / 2, y, 'L', x + w / 2, y + h - 1, 'M', x + w + 1, y, 'L', x + w, y + h,
                'M', x + w + 2, y + h, 'L', x + w, y + h + 2, 'L', x + w - 2, y + h, 'z'];
        };
        Highcharts.SVGRenderer.prototype.symbols["uclRightUNM"] = circleFunc;
        Highcharts.SVGRenderer.prototype.symbols["uclRightMasked"] = circleFunc;
        Highcharts.SVGRenderer.prototype.symbols["uclRightMaskedNR"] = circleFunc;
        Highcharts.SVGRenderer.prototype.symbols["uclRightMaskedUNM"] = circleFunc;

        // ucl left
        Highcharts.SVGRenderer.prototype.symbols["uclLeft"] = function (x, y, w, h) {
            return ['M', x - 1, y, 'L', x - 1, y + h, 'M', x - 1, y, 'L', x + w + 1, y, 'M', x + w / 2, y, 'L', x + w / 2, y + h - 1, 'M', x + w + 1, y, 'L', x + w, y + h, 'z'];
        };
        Highcharts.SVGRenderer.prototype.symbols["uclLeftNR"] = function (x, y, w, h) {
            return ['M', x - 1, y, 'L', x - 1, y + h, 'M', x - 1, y, 'L', x + w + 1, y, 'M', x + w / 2, y, 'L', x + w / 2, y + h - 1, 'M', x + w + 1, y, 'L', x + w, y + h,
                'M', x + w + 2, y + h, 'L', x + w, y + h + 2, 'L', x + w - 2, y + h, 'z'];
        };
        Highcharts.SVGRenderer.prototype.symbols["uclLeftUNM"] = circleFunc;
        Highcharts.SVGRenderer.prototype.symbols["uclLeftMasked"] = circleFunc;
        Highcharts.SVGRenderer.prototype.symbols["uclLeftMaskedNR"] = circleFunc;
        Highcharts.SVGRenderer.prototype.symbols["uclLeftMaskedUNM"] = circleFunc;


        // sf right
        Highcharts.SVGRenderer.prototype.symbols["sfRight"] = function (x, y, w, h) {
            return ['M', x + w / 2 + 2, y + 1, 'a', w / 2, h / 2, 0, 1, 0, 1, 1, 'M', x - 2, y + h + 2, 'L', x + w + 2, y - 2, 'z'];
        };
        Highcharts.SVGRenderer.prototype.symbols["sfRightNR"] = function (x, y, w, h) {
            return ['M', x + w / 2 + 2, y + 1, 'a', w / 2, h / 2, 0, 1, 0, 1, 1, 'M', x - 2, y + h + 2, 'L', x + w + 2, y - 2,
                'M', x + w / 4, y + h - (h / 4), 'L', x, y + h, 'M', x, y + h, x - 2, y + h - 2, 'L', x - 2, y + h + 2, 'L', x + 2, y + h + 2, 'z'];
        };
        Highcharts.SVGRenderer.prototype.symbols["sfRightUNM"] = circleFunc;
        Highcharts.SVGRenderer.prototype.symbols["sfRightMasked"] = circleFunc;
        Highcharts.SVGRenderer.prototype.symbols["sfRightMaskedNR"] = circleFunc;
        Highcharts.SVGRenderer.prototype.symbols["sfRightMaskedUNM"] = circleFunc;


        //sf left
        Highcharts.SVGRenderer.prototype.symbols["sfLeft"] = function (x, y, w, h) {
            return ['M', x + w + 2, y - 2, 'L', x - 2, y + h + 2, 'M', x - 2, y - 2, 'L', x + w + 2, y + h + 2, 'M', x - 1, y + (h / 2), 'L', x + (w / 2), y - 1, 'M', x + w / 2, y + h + 1, 'L', x + w + 2, y + h / 2 - 1, 'z'];
        };
        Highcharts.SVGRenderer.prototype.symbols["sfLeftNR"] = function (x, y, w, h) {
            return ['M', x + w + 2, y - 2, 'L', x - 2, y + h + 2, 'M', x - 2, y - 2, 'L', x + w + 2, y + h + 2, 'M', x - 1, y + (h / 2), 'L', x + (w / 2), y - 1, 'M', x + w / 2, y + h + 1, 'L', x + w + 2, y + h / 2 - 1,
                'M', x + w, y + h, 'L', x + w + 2, y + h - 1, 'L', x + w + 2, y + h + 2, 'L', x + w - 1, y + h + 2, 'z'];
        };
        Highcharts.SVGRenderer.prototype.symbols["sfLeftUNM"] = circleFunc;
        Highcharts.SVGRenderer.prototype.symbols["sfLeftMasked"] = circleFunc;
        Highcharts.SVGRenderer.prototype.symbols["sfLeftMaskedNR"] = circleFunc;
        Highcharts.SVGRenderer.prototype.symbols["sfLeftMaskedUNM"] = circleFunc;


        //sfa right
        Highcharts.SVGRenderer.prototype.symbols["sfaRight"] = function (x, y, w, h) {
            return ['M', x, y + h - 2, 'C', x + w / 2 - 2, y - 2, x + w / 2 + 2, y - 2, x + w, y + h - 2, 'M', x, y + h - 2, 'L', x, y + h + 1, 'M', x + w, y + h - 2, 'L', x + w, y + h + 1, 'M', x + w, y + h - 2, 'L', x, y + h - 2, 'z'];
        };
        Highcharts.SVGRenderer.prototype.symbols["sfaRightNR"] = function (x, y, w, h) {
            return ['M', x, y + h - 2, 'C', x + w / 2 - 2, y - 2, x + w / 2 + 2, y - 2, x + w, y + h - 2, 'M', x, y + h - 2, 'L', x, y + h + 1, 'M', x + w, y + h - 2, 'L', x + w, y + h + 1, 'M', x + w, y + h - 2, 'L', x, y + h - 2, 'z'];
        };
        Highcharts.SVGRenderer.prototype.symbols["sfaRightUNM"] = circleFunc;
        Highcharts.SVGRenderer.prototype.symbols["sfaRightMasked"] = circleFunc;
        Highcharts.SVGRenderer.prototype.symbols["sfaRightMaskedNR"] = circleFunc;
        Highcharts.SVGRenderer.prototype.symbols["sfaRightMaskedUNM"] = circleFunc;


        //sfa Left
        Highcharts.SVGRenderer.prototype.symbols["sfaLeft"] = function (x, y, w, h) {
            return ['M', x, y + h - 2, 'C', x + w / 2 - 2, y - 2, x + w / 2 + 2, y - 2, x + w, y + h - 2, 'M', x, y + h - 2, 'L', x, y + h + 1, 'M', x + w, y + h - 2, 'L', x + w, y + h + 1, 'M', x + w, y + h - 2, 'L', x, y + h - 2, 'z'];
        };
        Highcharts.SVGRenderer.prototype.symbols["sfaLeftNR"] = function (x, y, w, h) {
            return ['M', x, y + h - 2, 'C', x + w / 2 - 2, y - 2, x + w / 2 + 2, y - 2, x + w, y + h - 2, 'M', x, y + h - 2, 'L', x, y + h + 1, 'M', x + w, y + h - 2, 'L', x + w, y + h + 1, 'M', x + w, y + h - 2, 'L', x, y + h - 2, 'z'];
        };
        Highcharts.SVGRenderer.prototype.symbols["sfaLeftUNM"] = circleFunc;
        Highcharts.SVGRenderer.prototype.symbols["sfaLeftMasked"] = circleFunc;
        Highcharts.SVGRenderer.prototype.symbols["sfaLeftMaskedNR"] = circleFunc;
        Highcharts.SVGRenderer.prototype.symbols["sfaLeftMaskedUNM"] = circleFunc;
    }
}
